// Firestore access for the multi-moderator claiming protocol.
//
// Two subcollections under moderation/{eventId}:
//
//   moderators/{sessionId}  presence — heartbeat, free slots, challenge filter
//   claims/{videoId}        one document per claimed video
//
// The video id being the claim's document id is the whole trick: exclusivity
// falls out of Firestore's own document semantics instead of a convention two
// clients have to agree on. Everything here is client-side; there is no Cloud
// Function and no scheduled cleanup, so abandoned state has to expire rather
// than be swept.
//
// See docs/superpowers/specs/2026-09-04-multi-moderator-claiming-design.md

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore'
import { db } from '../firebase'
import { claimTtlMs } from '../constants/moderation'

//----------------------------------------------------------------------------
// Paths

function moderatorsCol(eventId) {
  return collection(db, 'moderation', eventId, 'moderators')
}

function moderatorDoc(eventId, sessionId) {
  return doc(db, 'moderation', eventId, 'moderators', sessionId)
}

function claimsCol(eventId) {
  return collection(db, 'moderation', eventId, 'claims')
}

function claimDoc(eventId, videoId) {
  return doc(db, 'moderation', eventId, 'claims', videoId)
}

//----------------------------------------------------------------------------
// Timestamps
//
// Firestore hands back a Timestamp for a materialized serverTimestamp and null
// for one that has not resolved yet (the local echo of our own pending write).
// Callers want milliseconds or nothing.

export function tsMillis(value) {
  if (!value) return null
  if (typeof value.toMillis === 'function') return value.toMillis()
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return value
  return null
}

//----------------------------------------------------------------------------
// Presence

/**
 * Write this session's presence. Called on the heartbeat cadence, not every
 * tick — peers never need a 1s-resolution view of each other's free slots.
 */
export async function writePresence(eventId, sessionId, presence) {
  await setDoc(
    moderatorDoc(eventId, sessionId),
    {
      sessionId,
      uid: presence.uid,
      displayName: presence.displayName || '',
      capacity: presence.capacity,
      freeSlots: presence.freeSlots,
      load: presence.load,
      challengeFilter: presence.challengeFilter || [],
      lastHeartbeat: serverTimestamp(),
    },
    { merge: true },
  )
}

export async function removePresence(eventId, sessionId) {
  await deleteDoc(moderatorDoc(eventId, sessionId))
}

export function subscribeModerators(eventId, callback) {
  return onSnapshot(moderatorsCol(eventId), (snap) => {
    callback(
      snap.docs.map((d) => {
        const data = d.data()
        return {
          ...data,
          sessionId: d.id,
          lastHeartbeatMs: tsMillis(data.lastHeartbeat),
        }
      }),
    )
  })
}

//----------------------------------------------------------------------------
// Claims

export function subscribeClaims(eventId, callback) {
  return onSnapshot(claimsCol(eventId), (snap) => {
    const claims = {}
    snap.docs.forEach((d) => {
      const data = d.data()
      claims[d.id] = {
        ...data,
        videoId: d.id,
        claimedAtMs: tsMillis(data.claimedAt),
      }
    })
    callback(claims)
  })
}

/**
 * Take a video, if it is genuinely free.
 *
 * `isClaimLive` decides whether an existing claim still counts — it needs the
 * caller's view of which sessions are alive and what the corrected clock says,
 * neither of which belongs in this module. Returns true when the claim is ours
 * afterwards.
 *
 * The transaction is what makes this safe: two moderators reaching for the same
 * video both read the same absent-or-expired claim, but only one commit lands,
 * and the other retries and sees the winner.
 */
export async function claimVideo(eventId, videoId, sessionId, uid, video, isClaimLive) {
  const ref = claimDoc(eventId, videoId)
  try {
    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref)
      if (snap.exists()) {
        const existing = { ...snap.data(), videoId, claimedAtMs: tsMillis(snap.data().claimedAt) }
        if (existing.sessionId !== sessionId && isClaimLive(existing)) return false
      }
      tx.set(ref, {
        sessionId,
        uid,
        claimedAt: serverTimestamp(),
        ttlMs: claimTtlMs(video),
      })
      return true
    })
  } catch {
    // A contended transaction that exhausts its retries is a lost race, not an
    // error worth surfacing: the next tick will look again.
    return false
  }
}

/**
 * Give up a claim. Guarded on ownership so releasing a video that has already
 * been taken over — which is exactly what a slow moderator does after losing a
 * race — cannot delete the new owner's claim.
 */
export async function releaseClaim(eventId, videoId, sessionId) {
  const ref = claimDoc(eventId, videoId)
  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref)
      if (!snap.exists()) return
      if (snap.data().sessionId !== sessionId) return
      tx.delete(ref)
    })
  } catch {
    /* best effort; the TTL is the guarantee */
  }
}

export async function releaseClaims(eventId, videoIds, sessionId) {
  await Promise.all(videoIds.map((id) => releaseClaim(eventId, id, sessionId)))
}

/**
 * Unguarded delete, for the page-teardown path where there is no time for a
 * transaction round-trip per video. Racing a new owner here is harmless: they
 * re-claim on their next tick.
 */
export function releaseClaimsFast(eventId, videoIds) {
  return Promise.all(videoIds.map((id) => deleteDoc(claimDoc(eventId, id)).catch(() => {})))
}

/** Drop claim documents for videos that are no longer pending. Hygiene only —
 *  availability already ignores them. */
export async function sweepOrphanClaims(eventId, orphanIds) {
  await Promise.all(orphanIds.map((id) => deleteDoc(claimDoc(eventId, id)).catch(() => {})))
}

/** Drop presence documents left by moderators that died without cleaning up.
 *  Also hygiene: peers stopped counting these long ago. */
export async function sweepDeadPresence(eventId, sessionIds) {
  await Promise.all(
    sessionIds.map((id) => deleteDoc(moderatorDoc(eventId, id)).catch(() => {})),
  )
}

/** One-shot read, for the harness and for diagnostics. */
export async function fetchClaims(eventId) {
  const snap = await getDocs(claimsCol(eventId))
  return snap.docs.map((d) => ({ videoId: d.id, ...d.data() }))
}
